import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TestingHqContext } from '../../context/TestingHqContext';
import { UserContext } from '../../context/UserContext';

import RoundSelector from '../../components/RoundSelector';
import { get_testing_place_list, get_testing_accept_count } from '../../utils/restapi';
import TESTING_ACCEPT_STATUS from '../../constants/testing_accept';

import './branch.css';

const REQUEST_STATUS = TESTING_ACCEPT_STATUS.field.join;            // 1: 접수신청
const ACCEPT_COMPLETE_STATUS = TESTING_ACCEPT_STATUS.field.decided; // 2: 접수 및 결제완료

/**
 * 회차별 접수현황
 *  선택한 회차·일정에 대해 내 지역본부 소속 고사장별로
 *  접수신청(1) / 접수완료(2) / 전체 인원을 보여준다.
 */
const BranchAcceptStat = () => {
  const { testingHqs } = useContext(TestingHqContext);
  const { user } = useContext(UserContext);

  const [selection, setSelection] = useState({ round: null, scheduleId: '' });
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const myHqCodes = useMemo(() => testingHqs.map((hq) => hq.code).filter(Boolean), [testingHqs]);
  const myHqCodesKey = myHqCodes.join(',');

  const handleSelectionChange = useCallback((next) => setSelection(next), []);
  const handleSelectorError = useCallback((message) => setErrorMessage(message), []);

  const testingId = selection.round?.testing_id;
  const typeCode = selection.round?.type;
  const scheduleId = selection.scheduleId;

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!testingId) {
        setRows([]);
        return;
      }
      if (!myHqCodes.length) {
        setRows([]);
        setErrorMessage('소속 지역본부 정보가 없습니다.');
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        // 카운트는 고사장·상태별 GROUP BY 집계.
        // 표시 대상 고사장만 내 지역본부 소속으로 제한하고(gcodes),
        // 해당 고사장으로 접수된 인원은 전원 집계한다.
        const countParams = { type: typeCode, testing_id: Number(testingId) };
        if (scheduleId) countParams.testing_schedule_id = Number(scheduleId);

        const [placeResponse, countResponse] = await Promise.all([
          get_testing_place_list({
            person_id: user?.person_id || 0,
            testing_id: Number(testingId),
            gcodes: myHqCodes.join(','),
            page: 1,
            rowsPerPage: 10000,
            sort: 'a_gcode,a_code',
            isAll: false,
          }),
          get_testing_accept_count(countParams),
        ]);

        if (cancelled) return;

        const places = placeResponse?.lists || [];
        const rowMap = new Map();
        // 접수 0명인 고사장도 표시
        places.forEach((place) => {
          const placeId = place.testing_place_id;
          if (!placeId) return;
          rowMap.set(Number(placeId), {
            testing_place_id: placeId,
            name: place.name || '-',
            requestCount: 0,
            acceptCompleteCount: 0,
            totalCount: 0,
          });
        });

        (countResponse?.lists || []).forEach((c) => {
          const row = rowMap.get(Number(c.testing_place_id));
          if (!row) return;
          const status = Number(c.status);
          const cnt = Number(c.cnt) || 0;
          if (status === REQUEST_STATUS) row.requestCount += cnt;
          else if (status >= ACCEPT_COMPLETE_STATUS) row.acceptCompleteCount += cnt;
          row.totalCount += cnt;
        });

        setRows(Array.from(rowMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko')));
      } catch (error) {
        console.error('Error fetching accept stat:', error);
        if (!cancelled) {
          setRows([]);
          setErrorMessage('접수 현황을 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingId, typeCode, scheduleId, myHqCodesKey]);

  const summary = useMemo(() => rows.reduce(
    (acc, row) => {
      acc.requestCount += row.requestCount;
      acc.acceptCompleteCount += row.acceptCompleteCount;
      acc.totalCount += row.totalCount;
      return acc;
    },
    { requestCount: 0, acceptCompleteCount: 0, totalCount: 0 }
  ), [rows]);

  return (
    <div id="branch_page">
      <div className="head">
        <h1>회차별 접수현황</h1>
      </div>
      <div className="body">
        <div className="top">
          <ul className="form">
            <RoundSelector onChange={handleSelectionChange} onError={handleSelectorError} />
          </ul>
        </div>

        <div className="stat_summary">
          <div className="summary_card">
            <strong>접수신청</strong>
            <span>{summary.requestCount}</span>
          </div>
          <div className="summary_card">
            <strong>접수완료</strong>
            <span>{summary.acceptCompleteCount}</span>
          </div>
          <div className="summary_card">
            <strong>전체(1+2)</strong>
            <span>{summary.totalCount}</span>
          </div>
        </div>

        <div className="middle stat_table_wrap">
          {errorMessage ? <p className="stat_error">{errorMessage}</p> : null}

          <table className="stat_table">
            <thead>
              <tr>
                <th>NO.</th>
                <th>고사장</th>
                <th>접수신청(1)</th>
                <th>접수완료(2)</th>
                <th>전체(1+2)</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr>
                  <td colSpan={5} className="stat_empty">
                    {isLoading ? '데이터를 불러오는 중입니다.' : '조회된 데이터가 없습니다.'}
                  </td>
                </tr>
              ) : null}

              {rows.map((row, index) => (
                <tr key={row.testing_place_id}>
                  <td>{index + 1}</td>
                  <td>{row.name}</td>
                  <td>{row.requestCount}</td>
                  <td>{row.acceptCompleteCount}</td>
                  <td>{row.totalCount}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="stat_total_row">
                <td colSpan={2}>합계</td>
                <td>{summary.requestCount}</td>
                <td>{summary.acceptCompleteCount}</td>
                <td>{summary.totalCount}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BranchAcceptStat;
